<template>
  <div class="signup-component w-full">
    <!-- Progress Indicator (Step indicator line on top) -->
    <div class="mb-6">
      <div class="flex items-center justify-between text-xs text-gray-500 font-medium mb-1.5">
        <span>{{ stepIndicatorText }}</span>
        <span>{{ currentStepIndex + 1 }} of {{ totalStepsCount }}</span>
      </div>
      <!-- 4-segment progress bar -->
      <div class="w-full h-1 bg-gray-100 rounded-full overflow-hidden flex">
        <div class="h-full bg-[#1877F2] transition-all duration-300 rounded-full"
          :style="{ width: progressPercentage + '%' }"></div>
      </div>
    </div>

    <!-- ========================================================================= -->
    <!-- STEP 1: USER INFORMATION -->
    <!-- ========================================================================= -->
    <div v-if="currentStep === 'user'" key="step-user" class="w-full">
      <div class="text-left mb-6">
        <h1 class="text-2xl sm:text-3xl font-black text-gray-900 mb-1 tracking-tight">
          Create your company account
        </h1>
        <p class="text-gray-500 text-sm">
          Tell us a little bit about yourself.
        </p>
      </div>

      <!-- 1. Sign up with Google -->
      <div class="mb-5 flex justify-center w-full">
        <GoogleLogin @google-login-success="handleGoogleLoginSuccess" :show-guest-login="false" :show-divider="false"
          button-text="signup_with" button-id="google-signup-btn" />
      </div>

      <!-- or use your email divider -->
      <div class="w-full flex items-center justify-center mb-5">
        <div class="flex-1 h-[1px] bg-gray-300"></div>
        <span class="px-3 text-xs text-gray-500 font-medium lowercase">or enter details</span>
        <div class="flex-1 h-[1px] bg-gray-300"></div>
      </div>

      <!-- First & Last Name -->
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        <div>
          <label class="block text-xs font-bold text-gray-800 mb-1.5">
            First Name <span class="text-red-500">*</span>
          </label>
          <input type="text" v-model="form.firstName" placeholder="e.g. John" @keyup.enter="validateUserAndProceed"
            class="w-full h-11 px-3.5 bg-white rounded-xl text-sm text-gray-900 outline-none transition-all placeholder:text-gray-400 auth-form-input"
            style="border: 1.5px solid #d1d5db !important; box-sizing: border-box;" />
        </div>
        <div>
          <label class="block text-xs font-bold text-gray-800 mb-1.5">
            Last Name <span class="text-red-500">*</span>
          </label>
          <input type="text" v-model="form.lastName" placeholder="e.g. Doe" @keyup.enter="validateUserAndProceed"
            class="w-full h-11 px-3.5 bg-white rounded-xl text-sm text-gray-900 outline-none transition-all placeholder:text-gray-400 auth-form-input"
            style="border: 1.5px solid #d1d5db !important; box-sizing: border-box;" />
        </div>
      </div>

      <!-- Mobile Number -->
      <div class="mb-4">
        <label class="block text-xs font-bold text-gray-800 mb-1.5">
          Mobile number <span class="text-red-500">*</span>
        </label>
        <div class="relative flex items-center rounded-xl overflow-hidden bg-white auth-form-input"
          style="border: 1.5px solid #d1d5db !important; box-sizing: border-box;">
          <div class="px-3.5 py-2.5 bg-gray-50 border-r border-gray-300 text-sm font-bold text-gray-700 select-none">
            +91
          </div>
          <input type="tel" v-model="form.mobile" maxlength="10" @input="debouncedCheckMobileAvailability"
            @keyup.enter="validateUserAndProceed" placeholder="Enter 10-digit number"
            class="w-full h-11 pl-3.5 pr-10 bg-transparent border-0 text-sm text-gray-900 outline-none placeholder:text-gray-400" />
          <div class="absolute right-3 top-1/2 -translate-y-1/2 flex items-center pointer-events-none">
            <Loader v-if="mobileChecking" :size="3" />
            <v-icon v-else-if="mobileChecked && !mobileAvailable" color="#ef4444" small>mdi-close-circle</v-icon>
            <v-icon v-else-if="mobileChecked && mobileAvailable" color="#10b981" small>mdi-check-circle</v-icon>
          </div>
        </div>
        <p v-if="mobileChecked && !mobileAvailable" class="text-xs text-red-500 mt-1">
          This mobile number is already registered. Please try another.
        </p>
      </div>

      <!-- Email ID -->
      <div class="mb-5">
        <label class="block text-xs font-bold text-gray-800 mb-1.5">
          Work Email <span class="font-normal text-gray-400">(optional)</span>
        </label>
        <input type="email" v-model="form.email" placeholder="name@company.com" @keyup.enter="validateUserAndProceed"
          class="w-full h-11 px-3.5 bg-white rounded-xl text-sm text-gray-900 outline-none transition-all placeholder:text-gray-400 auth-form-input"
          style="border: 1.5px solid #d1d5db !important; box-sizing: border-box;" />
      </div>

      <!-- Continue Button -->
      <button type="button" @click="validateUserAndProceed" :disabled="!isUserFormValid || !mobileAvailable"
        :loading="isLoading"
        class="w-full h-11 bg-[#1877F2] hover:bg-[#166fe5] active:bg-[#1465d2] disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl font-bold text-sm shadow-sm transition-all flex items-center justify-center border-none cursor-pointer select-none mb-4">
        <v-progress-circular v-if="isLoading" indeterminate color="white" size="18" width="2"
          class="mr-2"></v-progress-circular>
        <span>Continue</span>
        <v-icon right color="white" small class="ml-1">mdi-arrow-right</v-icon>
      </button>

      <!-- Explore demo workspace -->
      <div @click="exploreDemoWorkspace"
        class="w-full py-3 px-4 rounded-xl text-center text-xs text-gray-600 cursor-pointer transition-all select-none"
        style="border: 1.5px dashed #cbd5e1 !important; background-color: #f8fafc;">
        <span>Just looking around? </span>
        <span class="text-blue-600 font-bold hover:underline">Explore a demo workspace</span>
      </div>
    </div>

    <!-- ========================================================================= -->
    <!-- STEP 2: COMPANY INFORMATION -->
    <!-- ========================================================================= -->
    <div v-else-if="currentStep === 'company'" key="step-company" class="w-full">
      <div class="text-left mb-6">
        <h1 class="text-2xl sm:text-3xl font-black text-gray-900 mb-1 tracking-tight">
          {{ googleSignupData ? `Hello ${googleSignupData.name}` : 'Tell us about your company' }}
        </h1>
        <p class="text-gray-500 text-sm">
          We use this to set up your company workspace.
        </p>
      </div>

      <!-- Company Name -->
      <div class="mb-4">
        <label class="block text-xs font-bold text-gray-800 mb-1.5">
          Company Name <span class="text-red-500">*</span>
        </label>
        <div class="relative flex items-center rounded-xl bg-white auth-form-input overflow-hidden"
          style="border: 1.5px solid #d1d5db !important; box-sizing: border-box;">
          <input type="text" v-model="form.companyName" @input="handleCompanyNameInput"
            @keyup.enter="validateCompanyAndNext" placeholder="e.g. Acme Corp"
            class="w-full h-11 pl-3.5 pr-10 bg-transparent border-0 text-sm text-gray-900 outline-none placeholder:text-gray-400" />
          <div class="absolute right-3 top-1/2 -translate-y-1/2 flex items-center pointer-events-none">
            <Loader v-if="orgNameChecking" :size="3" />
            <v-icon v-else-if="orgNameChecked && !orgNameAvailable" color="#ef4444" small>mdi-close-circle</v-icon>
            <v-icon v-else-if="orgNameChecked && orgNameAvailable" color="#10b981" small>mdi-check-circle</v-icon>
          </div>
        </div>
        <p v-if="orgNameChecked && !orgNameAvailable" class="text-xs text-red-500 mt-1">
          This organization name is already taken. Please try another.
        </p>
      </div>

      <!-- Company Code -->
      <div class="mb-4">
        <!-- <div class="flex items-center justify-between mb-1.5">
          <label class="block text-xs font-bold text-gray-800">
            Company Code <span class="text-red-500">*</span>
          </label>
          <span class="text-[11px] text-gray-400">6 uppercase letters</span>
        </div>
        <div class="relative flex items-center rounded-xl bg-white auth-form-input overflow-hidden"
          style="border: 1.5px solid #d1d5db !important; box-sizing: border-box;">
          <input type="text" v-model="form.companyCode" maxlength="6" @input="handleCompanyCodeInput"
            @keyup.enter="validateCompanyAndNext" placeholder="e.g. ACMECO"
            class="w-full h-11 pl-3.5 pr-10 bg-transparent border-0 text-sm text-gray-900 outline-none uppercase placeholder:text-gray-400 font-semibold tracking-wider" />
          <div class="absolute right-3 top-1/2 -translate-y-1/2 flex items-center pointer-events-none">
            <Loader v-if="orgChecking" :size="3" />
            <v-icon v-else-if="orgChecked && !orgAvailable" color="#ef4444" small>mdi-close-circle</v-icon>
            <v-icon v-else-if="orgChecked && orgAvailable" color="#10b981" small>mdi-check-circle</v-icon>
          </div>
        </div> -->
        <p v-if="orgChecked && !orgAvailable" class="text-xs text-red-500 mt-1">
          This organization code is already taken. Please try another.
        </p>
      </div>

      <!-- Number of employees / Team Size -->
      <div class="mb-6">
        <label class="block text-xs font-bold text-gray-800 mb-2">
          Number of employees <span class="text-red-500">*</span>
        </label>
        <div class="grid grid-cols-2 gap-2">
          <button type="button" v-for="(size, index) in companySizes" :key="index" @click="form.companySize = size"
            class="h-10 px-3 rounded-xl text-xs font-semibold transition-all border flex items-center justify-center cursor-pointer select-none"
            :style="form.companySize === size
              ? 'background-color: #eff6ff; border-color: #2563eb !important; color: #1d4ed8;'
              : 'background-color: #ffffff; border-color: #d1d5db !important; color: #4b5563;'">
            {{ size }}
          </button>
        </div>
      </div>

      <!-- Continue Button -->
      <button type="button" @click="validateCompanyAndNext"
        :disabled="!isCompanyFormValid || !orgAvailable || !orgNameAvailable" :loading="isLoading"
        class="w-full h-11 bg-[#1877F2] hover:bg-[#166fe5] active:bg-[#1465d2] disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl font-bold text-sm shadow-sm transition-all flex items-center justify-center border-none cursor-pointer select-none mb-3">
        <v-progress-circular v-if="isLoading" indeterminate color="white" size="18" width="2"
          class="mr-2"></v-progress-circular>
        <span>{{ googleSignupData?.googleToken ? 'Submit' : 'Continue' }}</span>
        <v-icon right color="white" small class="ml-1">mdi-arrow-right</v-icon>
      </button>

      <!-- Back button -->
      <div class="text-center">
        <button type="button" @click="currentStep = 'user'"
          class="text-xs font-bold text-blue-600 hover:text-blue-700 bg-transparent border-none cursor-pointer py-2 px-4 select-none">
          Back
        </button>
      </div>
    </div>

    <!-- ========================================================================= -->
    <!-- STEP 3: SELECT USE CASES -->
    <!-- ========================================================================= -->
    <div v-else-if="currentStep === 'usecase-selection'" key="step-usecase" class="w-full">
      <div class="text-left mb-6">
        <h1 class="text-2xl sm:text-3xl font-black text-gray-900 mb-1 tracking-tight">
          Select Use Cases
        </h1>
        <p class="text-gray-500 text-sm">
          What problem are you looking to solve? (Select one or more)
        </p>
      </div>

      <div class="space-y-2.5 mb-6">
        <div v-for="useCaseItem in useCases" :key="useCaseItem.value" @click="toggleUseCase(useCaseItem.value)"
          class="w-full p-3.5 rounded-xl cursor-pointer transition-all flex items-center justify-between select-none"
          :style="uiUseCases.includes(useCaseItem.value)
            ? 'border: 2px solid #1877F2 !important; background-color: #f0f7ff;'
            : 'border: 1.5px solid #d1d5db !important; background-color: #ffffff;'">
          <div class="flex flex-col pr-2">
            <span class="text-sm font-bold"
              :class="uiUseCases.includes(useCaseItem.value) ? 'text-[#1877F2]' : 'text-gray-900'">
              {{ useCaseItem.text }}
            </span>
            <span class="text-xs text-gray-500 mt-0.5">
              {{ useCaseItem.description }}
            </span>
          </div>

          <v-icon :color="uiUseCases.includes(useCaseItem.value) ? '#1877F2' : '#9ca3af'" small>
            {{ uiUseCases.includes(useCaseItem.value) ? 'mdi-checkbox-marked-circle' :
              'mdi-checkbox-blank-circle-outline' }}
          </v-icon>
        </div>
      </div>

      <!-- Continue Button -->
      <button type="button" @click="validateUseCaseAndProceed" :disabled="!isUseCaseFormValid"
        class="w-full h-11 bg-[#1877F2] hover:bg-[#166fe5] active:bg-[#1465d2] disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl font-bold text-sm shadow-sm transition-all flex items-center justify-center border-none cursor-pointer select-none mb-3">
        <span>Continue</span>
        <v-icon right color="white" small class="ml-1">mdi-arrow-right</v-icon>
      </button>

      <!-- Back button -->
      <div class="text-center">
        <button type="button" @click="currentStep = 'company'"
          class="text-xs font-bold text-blue-600 hover:text-blue-700 bg-transparent border-none cursor-pointer py-2 px-4 select-none">
          Back
        </button>
      </div>
    </div>

    <!-- ========================================================================= -->
    <!-- STEP 4: SET PASSCODE -->
    <!-- ========================================================================= -->
    <div v-else-if="currentStep === 'set-passcode' && !googleSignupData" key="step-passcode" class="w-full">
      <div class="text-left mb-6">
        <h1 class="text-2xl sm:text-3xl font-black text-gray-900 mb-1 tracking-tight">
          Set Your Passcode
        </h1>
        <p class="text-gray-500 text-sm">
          Create a secure 6-digit passcode to log into your account.
        </p>
      </div>

      <!-- New Passcode -->
      <div class="mb-5">
        <div class="flex items-center justify-between mb-1.5">
          <label class="block text-xs font-bold text-gray-800">
            New Passcode
          </label>
          <button type="button" @click="showSetPasscode = !showSetPasscode"
            class="text-xs font-bold text-blue-600 hover:text-blue-700 bg-transparent border-none cursor-pointer select-none">
            {{ showSetPasscode ? 'Hide' : 'Show' }}
          </button>
        </div>
        <PasscodeInput v-model="form.setPasscode" :autoFocus="true" :hideToggleIcon="true"
          :isPasswordVisible="showSetPasscode" @keyup.enter.native="handleFinalizeSignup" />
      </div>

      <!-- Confirm Passcode -->
      <div class="mb-6">
        <div class="flex items-center justify-between mb-1.5">
          <label class="block text-xs font-bold text-gray-800">
            Confirm Passcode
          </label>
          <button type="button" @click="showConfirmPasscode = !showConfirmPasscode"
            class="text-xs font-bold text-blue-600 hover:text-blue-700 bg-transparent border-none cursor-pointer select-none">
            {{ showConfirmPasscode ? 'Hide' : 'Show' }}
          </button>
        </div>
        <PasscodeInput v-model="form.confirmPasscode" :hideToggleIcon="true" :isPasswordVisible="showConfirmPasscode"
          @keyup.enter.native="handleFinalizeSignup" />
      </div>

      <!-- Finalize Button -->
      <button type="button" @click="handleFinalizeSignup" :loading="isLoading || isPaymentProcessing"
        :disabled="!canSetPasscode"
        class="w-full h-11 bg-[#1877F2] hover:bg-[#166fe5] active:bg-[#1465d2] disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl font-bold text-sm shadow-sm transition-all flex items-center justify-center border-none cursor-pointer select-none mb-3">
        <v-progress-circular v-if="isLoading || isPaymentProcessing" indeterminate color="white" size="18" width="2"
          class="mr-2"></v-progress-circular>
        <span>{{ selectedPlan === 'Base' ? 'Create Account' : 'Pay & Create Account' }}</span>
        <v-icon right color="white" small class="ml-1">mdi-account-check</v-icon>
      </button>

      <!-- Back button -->
      <div class="text-center">
        <button type="button" @click="currentStep = 'usecase-selection'"
          class="text-xs font-bold text-blue-600 hover:text-blue-700 bg-transparent border-none cursor-pointer py-2 px-4 select-none">
          Back
        </button>
      </div>
    </div>

    <!-- Hidden / Lazy Razorpay -->
    <RazorpayCheckout ref="razorpayCheckout" :planUserName="`${form.firstName} ${form.lastName}`"
      :planUserEmail="form.email" :planUserContact="form.mobile" @success="handlePaymentSuccess"
      @error="handlePaymentFailure" @closed="isPaymentProcessing = false" />

    <!-- Toast Messages -->
    <div v-for="(alert, index) in alerts" :key="index" class="mt-4">
      <v-alert dense dismissible shaped text :type="alert.type" @input="removeAlert(index)">
        {{ alert.message }}
      </v-alert>
    </div>
  </div>
</template>

<script>
import { maskPhoneNumber } from '@/utils/common'
import { debounce } from '@/utils/common'
import GoogleLogin from '@/components/GoogleLogin.vue'
import PasscodeInput from '@/components/PasscodeInput.vue'
import Loader from '@/components/UI/Loader.vue'

export default {
  name: 'SignupComponent',
  components: {
    GoogleLogin,
    PasscodeInput,
    Loader,
    RazorpayCheckout: () => import('@/components/common/RazorpayCheckout.vue')
  },
  data() {
    return {
      currentStep: 'user', // 'user' (Step 1), 'company' (Step 2), 'usecase-selection' (Step 3), 'set-passcode' (Step 4)
      isLoading: false,
      otpCounter: 0,
      otpTimer: null,
      mobileChecking: false,
      mobileChecked: false,
      mobileAvailable: true,
      orgChecking: false,
      orgChecked: false,
      orgAvailable: true,
      orgNameChecking: false,
      orgNameChecked: false,
      orgNameAvailable: true,
      steps: [
        { key: 'user', label: 'User Info' },
        { key: 'company', label: 'Company Info' },
        { key: 'usecase-selection', label: 'Select Use Cases' },
        { key: 'set-passcode', label: 'Set Passcode' }
      ],
      uiUseCases: ['hrms'],
      showSetPasscode: false,
      showConfirmPasscode: false,
      form: {
        companyName: '',
        companyCode: '',
        companySize: 'Less than 25',
        useCase: ['hrms'],
        firstName: '',
        lastName: '',
        mobile: '',
        email: '',
        employeeCode: 'ADMIN1',
        otp: '',
        setPasscode: '',
        confirmPasscode: '',
        passCode: ''
      },
      selectedPlan: 'Base',
      isPaymentProcessing: false,
      plans: [
        {
          name: 'Base',
          price: 0,
          users: '10 Users',
          description: 'Free'
        },
        {
          name: 'Silver',
          price: 299,
          users: 'Up to 50 Users'
        },
        {
          name: 'Gold',
          price: 499,
          users: 'Up to 200 Users'
        },
        {
          name: 'Diamond',
          price: 599,
          users: 'Unlimited Users'
        }
      ],
      alerts: [],
      companySizes: ['Less than 25', '25-50', '51-100', 'More than 100'],
      useCases: [
        {
          text: 'HRMS',
          value: 'hrms',
          description: 'Manage and track HR processes'
        },
        {
          text: 'Complaint Management',
          value: 'complaint_management',
          description: 'Manage and track customer complaints and tickets'
        },
        {
          text: 'Lead Management',
          value: 'lead_management',
          description: 'Track leads, opportunities, and sales pipeline'
        },
        {
          text: 'Collection Management',
          value: 'collection_management',
          description: 'Manage and track debt collection processes'
        },
        {
          text: 'Loan Management',
          value: 'loan_management',
          description: 'Handle loan applications, disbursements, and repayments'
        }
      ],
      rules: {
        required: (value) => !!value || 'This field is required',
        companyCode: (value) =>
          (value &&
            value.length === 6 &&
            value === value.toUpperCase() &&
            /^[A-Z]+$/.test(value)) ||
          'Code must be 6 uppercase letters only',
        mobile: (value) =>
          (value && value.length === 10 && /^\d+$/.test(value)) ||
          'Enter a valid 10-digit mobile number'
      },
      googleSignupData: null
    }
  },
  computed: {
    currentStepIndex() {
      const idx = this.steps.findIndex((s) => s.key === this.currentStep)
      return idx >= 0 ? idx : 0
    },
    totalStepsCount() {
      return this.googleSignupData ? 3 : 4
    },
    stepIndicatorText() {
      const stepMap = {
        user: 'Step 1: Your Account',
        company: 'Step 2: Company Details',
        'usecase-selection': 'Step 3: Select Use Cases',
        'set-passcode': 'Step 4: Set Passcode'
      }
      return stepMap[this.currentStep] || 'Create Company'
    },
    progressPercentage() {
      const stepPct = {
        user: 25,
        company: 50,
        'usecase-selection': 75,
        'set-passcode': 100
      }
      return stepPct[this.currentStep] || 25
    },
    isUserFormValid() {
      if (this.googleSignupData && this.googleSignupData.email) return true
      return Boolean(
        this.form.firstName &&
        this.form.lastName &&
        this.form.mobile &&
        this.form.mobile.length === 10 &&
        /^\d+$/.test(this.form.mobile) &&
        this.form.employeeCode
      )
    },
    isCompanyFormValid() {
      return Boolean(
        this.form.companyName &&
        this.orgNameAvailable &&
        this.form.companyCode &&
        this.form.companyCode.length === 6 &&
        this.form.companyCode === this.form.companyCode.toUpperCase() &&
        /^[A-Z]+$/.test(this.form.companyCode) &&
        this.orgAvailable &&
        this.form.companySize
      )
    },
    isUseCaseFormValid() {
      const extraUseCases = (this.uiUseCases || []).filter((u) => u !== 'hrms')
      return this.uiUseCases && this.uiUseCases.includes('hrms') && extraUseCases.length >= 1
    },
    canSetPasscode() {
      return Boolean(
        this.form.setPasscode &&
        this.form.confirmPasscode &&
        this.form.setPasscode.length === 6 &&
        this.form.confirmPasscode.length === 6 &&
        this.form.setPasscode === this.form.confirmPasscode
      )
    }
  },
  created() {
    this.debouncedCheckMobileAvailability = debounce(this.checkMobileAvailability, 500)
    this.debouncedCheckOrgAvailability = debounce(this.checkOrgAvailability, 500)
    this.debouncedCheckOrgNameAvailability = debounce(this.checkOrgNameAvailability, 500)
  },
  methods: {
    maskNumber(num) {
      return maskPhoneNumber(String(num))
    },
    openPrivacyPolicy() {
      window.open('https://cualitywork.com/privacy', '_blank')
    },
    exploreDemoWorkspace() {
      this.showToast('Redirecting to demo workspace...', 'info')
      setTimeout(() => {
        window.open('https://cualitywork.com/demo', '_blank')
      }, 500)
    },
    handleCompanyCodeInput() {
      this.form.companyCode = this.form.companyCode.replace(/[^A-Za-z]/g, '').toUpperCase()
      if (this.form.companyCode && this.form.companyCode.length === 6) {
        this.debouncedCheckOrgAvailability()
      }
    },
    toggleUseCase(value) {
      if (value === 'hrms') {
        this.showToast('HRMS is selected by default', 'info')
        return
      }
      const index = this.uiUseCases.indexOf(value)
      if (index > -1) {
        this.uiUseCases.splice(index, 1)
      } else {
        this.uiUseCases.push(value)
      }
      this.updateMappedUseCases()
    },
    updateMappedUseCases() {
      const mapped = this.uiUseCases.map((item) => {
        if (item === 'complaint_management' || item === 'lead_management') {
          return 'base'
        }
        return item
      })
      this.form.useCase = Array.from(new Set(mapped))
    },
    handleCompanyNameInput() {
      if (this.form.companyName) {
        this.debouncedCheckOrgNameAvailability()
        this.generateCompanyCode()
      }
    },
    generateCompanyCode() {
      if (this.form.companyName) {
        this.form.companyCode = this.form.companyName
          .replace(/\s+/g, '')
          .substring(0, 6)
          .toUpperCase()
          .replace(/[^A-Z]/g, '')
      } else {
        this.form.companyCode = ''
      }
      if (this.form.companyCode && this.form.companyCode.length === 6) {
        this.debouncedCheckOrgAvailability()
      }
    },
    async checkOrgNameAvailability() {
      if (!this.form.companyName) return
      this.orgNameChecking = true
      this.orgNameChecked = false
      this.orgNameAvailable = true
      try {
        const res = await this.$axios.post('/cualitywork/orgname-availability', {
          name: this.form.companyName
        })
        this.orgNameAvailable = res.data?.available
        this.orgNameChecked = true
        if (!this.orgNameAvailable) {
          this.showToast('This organization name is already taken', 'error')
        }
      } catch (error) {
        this.orgNameAvailable = error.response?.data?.available || false
        this.orgNameChecked = true
      } finally {
        this.orgNameChecking = false
      }
    },
    async checkOrgAvailability() {
      if (!this.form.companyCode || this.form.companyCode.length !== 6) return
      this.orgChecking = true
      this.orgChecked = false
      this.orgAvailable = true
      try {
        const res = await this.$axios.post('/cualitywork/orgcode-availability', {
          code: this.form.companyCode
        })
        this.orgAvailable = res.data?.available
        this.orgChecked = true
        if (!this.orgAvailable) {
          this.showToast('This organization code is already taken', 'error')
        }
      } catch (error) {
        this.orgAvailable = error.response?.data?.available || false
        this.orgChecked = true
      } finally {
        this.orgChecking = false
      }
    },
    async checkMobileAvailability() {
      this.mobileChecked = false
      this.mobileAvailable = true
      if (!this.form.mobile || this.form.mobile.length !== 10 || !/^\d+$/.test(this.form.mobile)) {
        return
      }
      this.mobileChecking = true
      try {
        const res = await this.$axios.post('/cualitywork/mobile-availability', {
          mobile: Number(this.form.mobile)
        })
        this.mobileAvailable = res.data?.available
        this.mobileChecked = true
        if (!this.mobileAvailable) {
          this.showToast('This mobile number is already registered', 'error')
        }
      } catch (error) {
        this.mobileAvailable = error.response?.data?.available || false
        this.mobileChecked = true
      } finally {
        this.mobileChecking = false
      }
    },
    async validateUserAndProceed() {
      if (!this.isUserFormValid) {
        this.showToast('Please fill all required user information', 'error')
        return
      }
      if (!this.mobileAvailable && !(this.googleSignupData && this.googleSignupData?.email)) {
        this.showToast('Please use a different mobile number', 'error')
        return
      }
      this.currentStep = 'company'
    },
    async validateCompanyAndNext() {
      if (!this.isCompanyFormValid) {
        this.showToast('Please fill all required company information', 'error')
        return
      }
      if (!this.orgNameAvailable) {
        this.showToast('Please use a different organization name', 'error')
        return
      }
      if (!this.orgAvailable) {
        this.showToast('Please use a different organization code', 'error')
        return
      }
      this.currentStep = 'usecase-selection'
    },
    async validateUseCaseAndProceed() {
      if (!this.isUseCaseFormValid) {
        this.showToast('Please select at least one additional use case besides HRMS', 'error')
        return
      }
      try {
        if (this.googleSignupData && this.googleSignupData?.googleToken) {
          await this.checkPlanAndProceed()
        } else {
          this.currentStep = 'set-passcode'
        }
      } catch (error) {
        console.error(error)
        this.showToast('Something went wrong', 'error')
      }
    },
    async handleFinalizeSignup() {
      if (!this.canSetPasscode) {
        if (this.form.setPasscode !== this.form.confirmPasscode) {
          this.showToast('Passcodes do not match', 'error')
        } else {
          this.showToast('Passcode must be exactly 6 digits', 'error')
        }
        return
      }
      await this.setNewPasscode()
      await this.checkPlanAndProceed()
    },
    async checkPlanAndProceed() {
      await this.executeCompleteSignup()
    },
    selectPlan(plan) {
      this.selectedPlan = plan.name
    },
    async setNewPasscode() {
      this.form.passCode = this.form.setPasscode
      return Promise.resolve()
    },
    async executeCompleteSignup() {
      try {
        this.isLoading = true
        const plan = this.plans.find((p) => p.name === this.selectedPlan)
        if (!plan) throw new Error('Invalid plan selected')

        let planId
        let organizationId
        let userId

        if (plan.price === 0) {
          planId = await this.createPlan()
          organizationId = await this.createOrganization(planId)
          userId = await this.createEmployee(organizationId)
          await this.assignRoleToEmployee(userId, organizationId)
        } else {
          this.isPaymentProcessing = true
          const isLoaded = await this.$refs.razorpayCheckout.loadRazorpay()
          if (!isLoaded)
            throw new Error('Razorpay SDK failed to load. Please check your internet connection.')

          const planAmount = plan.price * 100
          const orderResponse = await this.$axios.post('/cualitywork/create-signup-order', {
            amount: planAmount,
            currency: 'INR',
            orgName: this.form.companyName || 'New Company'
          })

          if (!orderResponse.data.success) {
            throw new Error('Failed to create payment order')
          }
          const { order } = orderResponse.data

          await new Promise((resolve, reject) => {
            const options = {
              key: 'rzp_test_RsjI2YFWzhYic2',
              amount: order.amount,
              currency: order.currency,
              name: 'CualityWork',
              description: `Subscription for ${plan.name}`,
              order_id: order.id,
              prefill: {
                name: this.form.firstName + ' ' + this.form.lastName,
                email: this.form.email,
                contact: this.form.mobile
              },
              theme: { color: '#3399cc' },
              handler: async (response) => {
                try {
                  const customerRes = await this.$axios.post(
                    '/cualitywork/create-signup-customer',
                    {
                      orgName: this.form.companyName,
                      contactName: this.form.firstName + ' ' + this.form.lastName,
                      contactEmail: this.form.email,
                      contactMobile: this.form.mobile
                    }
                  )
                  const newCustomerId = customerRes.data.customerId
                  planId = await this.createPlan()
                  organizationId = await this.createOrganization(planId)
                  userId = await this.createEmployee(organizationId)
                  await this.assignRoleToEmployee(userId, organizationId)

                  await this.$axios.post('/cualitywork/verify-signup-payment', {
                    razorpay_order_id: response.razorpay_order_id,
                    razorpay_payment_id: response.razorpay_payment_id,
                    razorpay_signature: response.razorpay_signature,
                    orgId: organizationId,
                    planId: planId,
                    amount: planAmount,
                    customerId: newCustomerId
                  })

                  resolve()
                } catch (err) {
                  reject(new Error('Post-payment setup failed: ' + err.message))
                }
              },
              modal: {
                ondismiss: function () {
                  reject(new Error('Payment cancelled by user'))
                }
              }
            }

            const rzp1 = new window.Razorpay(options)
            rzp1.on('payment.failed', function (response) {
              reject(new Error(response.error.description))
            })
            rzp1.open()
          })
          this.isPaymentProcessing = false
        }

        await this.handleSubmitQuery(organizationId)
        this.showToast('Account created successfully! Logging you in...', 'success')

        localStorage.setItem('needsInitialSetup', 'true')
        const selectedUseCases = this.form.useCase.filter((uc) => uc !== 'hrms')
        localStorage.setItem('setupUseCase', JSON.stringify(selectedUseCases))

        if (this.googleSignupData && this.googleSignupData.googleToken) {
          await this.loginGoogleUser()
        } else {
          await this.loginUser()
        }
        this.isLoading = false
      } catch (error) {
        console.error('Signup process failed:', error)
        this.isLoading = false
        this.isPaymentProcessing = false
        this.showToast(
          error.message || 'Failed to complete signup process. Please try again.',
          'error'
        )
      }
    },
    async loginGoogleUser() {
      try {
        this.$emit('google-login', {
          token: this.googleSignupData?.googleToken,
          user: { name: this.googleSignupData?.name, email: this.googleSignupData?.email }
        })
      } catch (error) {
        console.error('Login failed:', error)
        this.showToast('Failed to login. Please try logging in manually.', 'error')
      }
    },
    async loginUser() {
      try {
        this.$emit('signup-complete', {
          mobile: this.form.mobile,
          passcode: this.form.passCode,
          organization_code: this.form.companyCode
        })
      } catch (error) {
        console.error('Login failed:', error)
        this.showToast('Failed to login. Please try logging in manually.', 'error')
      }
    },
    async createPlan() {
      try {
        const primaryUseCase = Array.isArray(this.form.useCase)
          ? (this.form.useCase[1] || this.form.useCase[0] || 'hrms')
          : (this.form.useCase || 'hrms')
        const planResponse = await this.$axios.post('/cualitywork/open-plan', {
          planName: `${this.form.companyName} Plan`,
          planDescription: `Default plan for ${this.form.companyName}`,
          useCase: primaryUseCase,
          selectMonths: 12
        })
        return planResponse.data?.plan?.plan_id
      } catch (error) {
        console.error('Failed to create plan:', error)
        throw new Error('Failed to create plan')
      }
    },
    async createOrganization(planId) {
      try {
        const now = new Date()
        const nextMonth = new Date()
        nextMonth.setMonth(now.getMonth() + 1)

        const validFrom = now.toISOString().split('T')[0]
        const validTo = nextMonth.toISOString().split('T')[0]

        const orgResponse = await this.$axios.post('/cualitywork/open-org', {
          name: this.form.companyName,
          code: this.form.companyCode,
          valid_from: validFrom,
          valid_to: validTo,
          plan_id: planId
        })
        return orgResponse.data?.organization?.organization_id
      } catch (error) {
        console.error('Failed to create organization:', error)
        throw new Error('Failed to create organization')
      }
    },
    async createEmployee(organizationId) {
      try {
        const payload = {
          first_name: this.form?.firstName || this.googleSignupData?.firstName,
          last_name: this.form?.lastName || this.googleSignupData?.lastName || undefined,
          mobile: Number(this.form?.mobile) || undefined,
          email: this.form?.email || this.googleSignupData?.email || undefined,
          employee_id: this.form?.employeeCode || 'ADMIN1',
          organization_id: organizationId,
          passcode: this.form?.passCode || undefined,
          is_google_logged_in: this.googleSignupData?.googleToken ? 1 : undefined,
          is_email_verified: this.googleSignupData?.email ? 1 : undefined
        }
        const employeeResponse = await this.$axios.post('/cualitywork/open-employee', payload)
        return employeeResponse.data?.data?.systemUserId
      } catch (error) {
        console.error('Failed to create employee:', error)
        throw new Error('Failed to create employee')
      }
    },
    async assignRoleToEmployee(userId, organizationId) {
      try {
        await this.$axios.post(`/cualitywork/assign-role/${userId}`, {
          organization_id: organizationId
        })
      } catch (error) {
        console.error('Failed to assign role to employee:', error)
        throw new Error('Failed to assign role to employee')
      }
    },
    async handleSubmitQuery(orgId) {
      try {
        const formData = new FormData()
        const details = `New Organization created successfully for ${this.form?.companyName} with code ${this.form?.companyCode} and employee code ${this.form?.employeeCode}. Size of company is ${this.form?.companySize}.`
        const fullName = this.form?.firstName
          ? `${this.form?.firstName} ${this.form?.lastName || ''}`
          : this.googleSignupData?.name
        const email = this.form?.email || this.googleSignupData?.email
        const mobile = this.form?.mobile || 'N/A'
        const primaryUseCase = Array.isArray(this.form.useCase)
          ? (this.form.useCase[1] || this.form.useCase[0] || 'hrms')
          : (this.form.useCase || 'hrms')
        formData.append('typeOfQuery', 'NEW_ORG_SETUP')
        formData.append('details', details)
        formData.append('companyName', this.form.companyName)
        formData.append('contactPerson', fullName)
        formData.append('contactEmail', email)
        formData.append('contactNumber', mobile)
        formData.append('sizeOfCompany', this.form.companySize)
        formData.append('useCase', primaryUseCase)
        formData.append('orgId', orgId)
        const response = await this.$axios.post('/cualitywork/query', formData)

        if (response.data.success) {
          this.$toast.success(response?.data?.message)
        }
      } catch (error) {
        const errorMessage = error?.response?.data?.message || 'Something went wrong'
        this.$toast.error(errorMessage)
      }
    },
    async handleGoogleLoginSuccess(googleResponse) {
      const { token, user } = googleResponse
      const checkUser = await this.checkIsUserExist(user.email)

      if (checkUser) {
        this.$emit('google-login', googleResponse)
      } else {
        this.googleSignupData = {
          name: user.name,
          email: user.email,
          firstName: user?.given_name,
          lastName: user?.family_name,
          googleToken: token
        }
        this.form.email = user.email || ''
        this.form.firstName = user.given_name || 'User'
        this.form.lastName = user.family_name || ''
        // Advance to step 2: Company Information
        this.currentStep = 'company'
        this.showToast('Google account connected! Please enter company details.', 'success')
      }
    },
    async checkIsUserExist(email) {
      try {
        const response = await this.$axios.post('/cualitywork/ext-user-details', {
          username: email
        })
        return response?.status ? true : false
      } catch (error) {
        console.error('Error checking user exist:', error)
        return false
      }
    },
    handlePaymentSuccess(response) {
      console.log('Payment success', response)
      this.isPaymentProcessing = false
    },
    handlePaymentFailure(error) {
      console.error('Payment failure', error)
      this.isPaymentProcessing = false
      this.showToast('Payment failed. Please try again.', 'error')
    },
    showToast(message, type = 'info') {
      const alert = { message, type }
      this.alerts.push(alert)
      setTimeout(() => {
        this.removeAlert(this.alerts.indexOf(alert))
      }, 5000)
    },
    removeAlert(index) {
      if (index > -1) {
        this.alerts.splice(index, 1)
      }
    }
  },
  beforeDestroy() {
    clearInterval(this.otpTimer)
  }
}
</script>

<style scoped>
.auth-form-input {
  transition: all 0.2s ease-in-out;
}

.auth-form-input:focus,
.auth-form-input:focus-within {
  border-color: #2563eb !important;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15) !important;
}
</style>
